import { safeGet } from "./api";

// Visitor country (ISO code) so Shopify Markets can return local prices and currency.
// Priority: ?country=XX in the URL (for testing) > cached value > Cloudflare geo lookup.
const KEY = "fm_country";
const MAX_AGE = 1000 * 60 * 60 * 24;
const valid = (c) => (/^[A-Z]{2}$/.test(c || "") ? c : null);

const readCache = () => {
  try {
    const { c, t } = JSON.parse(localStorage.getItem(KEY) || "{}");
    return Date.now() - t < MAX_AGE ? valid(c) : null;
  } catch {
    return null;
  }
};

const writeCache = (c) => {
  try { localStorage.setItem(KEY, JSON.stringify({ c, t: Date.now() })); } catch { /* storage unavailable */ }
};

const lookup = async (url) => {
  const res = await fetch(url, { cache: "no-store" });
  const loc = (await res.text()).match(/^loc=([A-Z]{2})$/m)?.[1];
  if (!valid(loc) || loc === "XX") throw new Error("no country");
  return loc;
};

let countryPromise;
export const getCountry = () => {
  if (!countryPromise) {
    countryPromise = (async () => {
      const fromUrl = valid(new URLSearchParams(window.location.search).get("country")?.toUpperCase());
      if (fromUrl) { writeCache(fromUrl); return fromUrl; }
      const cached = readCache();
      if (cached) return cached;
      try {
        const c = await lookup("/cdn-cgi/trace").catch(async () => {
          const shop = await safeGet("/shop", null);
          if (!shop?.checkout_domain) throw new Error("no shop");
          return lookup(`https://${shop.checkout_domain}/cdn-cgi/trace`);
        });
        writeCache(c);
        return c;
      } catch {
        return null;
      }
    })();
  }
  return countryPromise;
};

// Manual choice from the footer selector; remembered like a detected country.
export const chooseCountry = (c) => {
  writeCache(c);
  countryPromise = Promise.resolve(c);
  return c;
};

// "89 CHF" for Swiss francs (existing site style), local symbol format for everything else.
export const formatMoney = (amount, currency = "CHF") => {
  const n = Math.round(Number(amount ?? 0) * 100) / 100;
  const digits = n % 1 ? 2 : 0;
  if (currency === "CHF") return `${n.toFixed(digits)} CHF`;
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${n.toFixed(digits)} ${currency}`;
  }
};
